import { makeStyles, Theme } from '@material-ui/core';
import { DashCamList, SemanticDarkColors } from '@thingsw/pitta-modules';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../features/store';
import { THEME } from '../../features/Theme/slice';
import { Typography, Avatars } from '@thingsw/pitta-design-system';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import _ from 'lodash';
import { CAMERA } from '../../features/Camera/slice';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) => ({
  status: {
    // mantis 10483, 피그마 간격 적용하여 padding변경 (Leehj)
    padding: '16px 16px 16px',
    // padding: "12px 21px 16px",
    display: 'flex',
    justifyContent: 'space-between',
  },
  noti: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    maxWidth: '100%',
    justifyContent: 'space-between',
  },
  groupTitle: {
    ...(theme.direction === 'rtl'
      ? {
          marginLeft: 2,
          marginRight: 4,
        }
      : { marginLeft: 4, marginRight: 2 }),
    letterSpacing: -0.5,
    flex: '1 1 auto',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  arrow: {
    cursor: 'pointer',
    fill: (props: any) => props.colors.primary['1'],
    '&:hover': {
      fill: (props: any) => props.colors.primary['2'],
    },
    flex: '0 0 auto',
  },
  profile: {
    padding: '12px 12px 8px',
    // mantis 10483, 피그마 간격 적용하여 marginInlineEnd: 8->4로 변경 (Leehj)
    marginInlineEnd: 4,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  avatar: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: 'transparent !important',
  },
  cameraCountDiv: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0 8px',
    justifyContent: 'center',
  },
  avatarMulti2: {
    width: 25,
    height: 25,
    position: 'absolute',
    borderRadius: '50%',
    backgroundColor: 'transparent !important',
  },
  closeHeader: {
    margin: '8px 12px',
    boxShadow:
      'rgba(0, 0, 0, 0.12) -2px -2px 12px -4px, rgba(0, 0, 0, 0.16) 0px -2px 16px -4px',
    borderRadius: 16,
    border: (props: any) => `1px solid ${props.colors.primary['6']}`,
  },
  openHeader: {
    margin: '8px 12px 0',
    borderRadius: '16px 16px 0 0',
  },
}));
interface CameraGroupHeaderProps {
  mobile?: boolean;
  group: DashCamList;
  onOpen: VoidFunction;
  onClose: VoidFunction;
  initialOpen?: boolean;
}
export const CameraGroupHeader = ({
  mobile,
  group,
  onOpen,
  onClose,
  initialOpen,
}: CameraGroupHeaderProps) => {
  const { t } = useTranslation();

  const { colors, color } = useSelector((state: RootState) => state[THEME]);
  const { cameraList } = useSelector((state: RootState) => state[CAMERA]);
  const classes = useStyles({ colors, color });
  const [open, setOpen] = React.useState(initialOpen ?? false);

  useEffect(() => {
    setOpen(initialOpen ?? false);
  }, [initialOpen]);

  const groupCams = useMemo(() => {
    return _.filter(
      cameraList?.deviceListInfo,
      (dev) => dev.device.reg_category !== 'wifi'
    );
  }, [cameraList?.deviceListInfo]);

  const avartarDivWidth = useMemo(() => {
    const cams = _.filter(
      groupCams,
      (d) => d.groupManagementID === group.GroupID
    );

    if (cams.length === 1) {
      return 28;
    } else if (cams.length === 2) {
      return 40;
    } else if (cams.length === 3) {
      return 52;
    } else {
      return 64;
    }
  }, [group.GroupID, groupCams]);

  const avatarMarkup = useMemo(() => {
    const cams = _.filter(
      groupCams,
      (d) => d.groupManagementID === group.GroupID
    );
    const firstAvatar = _.find(
      groupCams,
      (dev) =>
        dev.device.psn === cams[0]?.device.psn &&
        dev.device.reg_category !== 'wifi'
    )?.device;
    const secondsAvatar = _.find(
      groupCams,
      (dev) =>
        dev.device.psn === cams[1]?.device.psn &&
        dev.device.reg_category !== 'wifi'
    )?.device;
    const thirdAvatar = _.find(
      groupCams,
      (dev) =>
        dev.device.psn === cams[2]?.device.psn &&
        dev.device.reg_category !== 'wifi'
    )?.device;
    const fourthAvatar = _.find(
      groupCams,
      (dev) =>
        dev.device.psn === cams[3]?.device.psn &&
        dev.device.reg_category !== 'wifi'
    )?.device;

    const noneAvatar = (
      <svg
        width='24'
        height='24'
        viewBox='0 0 24 24'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <g clipPath='url(#clip0_67_2062)'>
          <ellipse cx='12' cy='12' rx='12' ry='12' fill='white' />
          <path
            d='M8.47995 17.6277C8.13196 17.3493 7.9837 16.9019 8.06253 16.4633C8.20398 15.6764 8.58893 14.9448 9.17353 14.3721C9.92367 13.6373 10.9411 13.2245 12.002 13.2245C13.0628 13.2245 14.0802 13.6373 14.8304 14.3721C15.415 14.9448 15.7999 15.6764 15.9414 16.4633C16.0202 16.9019 15.8719 17.3493 15.524 17.6277C14.9663 18.0738 13.8732 18.6666 12.002 18.6666C10.1307 18.6666 9.03763 18.0738 8.47995 17.6277ZM12.002 12.7347C10.3445 12.7347 9.00195 11.4196 9.00195 9.79593C9.00195 8.17227 10.3445 6.85718 12.002 6.85718C13.6594 6.85718 15.0019 8.17227 15.0019 9.79593C15.0019 11.4196 13.6594 12.7347 12.002 12.7347Z'
            fill='#61007D'
          />
          <circle
            cx='12'
            cy='12'
            r='11.2'
            stroke={colors.primary['6']}
            strokeWidth='1.6'
          />
        </g>
        <defs>
          <clipPath id='clip0_67_2062'>
            <rect width='24' height='24' rx='12' fill='white' />
          </clipPath>
        </defs>
      </svg>
    );

    if (firstAvatar && secondsAvatar && thirdAvatar && fourthAvatar) {
      return (
        <>
          {fourthAvatar?.profile_img_v2.includes('json') ? (
            <div
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 0 }}
            >
              {noneAvatar}
            </div>
          ) : (
            <Avatars
              colors={colors}
              mode='camera'
              name={fourthAvatar?.dev_name}
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 0 }}
              imgSrc={fourthAvatar?.profile_img_v2 || undefined}
            />
          )}
          {thirdAvatar?.profile_img_v2.includes('json') ? (
            <div
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 12 }}
            >
              {noneAvatar}
            </div>
          ) : (
            <Avatars
              colors={colors}
              mode='camera'
              name={thirdAvatar?.dev_name}
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 12 }}
              imgSrc={thirdAvatar?.profile_img_v2 || undefined}
            />
          )}
          {secondsAvatar?.profile_img_v2.includes('json') ? (
            <div
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 24 }}
            >
              {noneAvatar}
            </div>
          ) : (
            <Avatars
              colors={colors}
              mode='camera'
              name={secondsAvatar?.dev_name}
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 24 }}
              imgSrc={secondsAvatar?.profile_img_v2 || undefined}
            />
          )}
          {firstAvatar?.profile_img_v2.includes('json') ? (
            <div
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 36 }}
            >
              {noneAvatar}
            </div>
          ) : (
            <Avatars
              colors={colors}
              mode='camera'
              name={firstAvatar?.dev_name}
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 36 }}
              imgSrc={firstAvatar?.profile_img_v2 || undefined}
            />
          )}
        </>
      );
    }

    if (firstAvatar && secondsAvatar && thirdAvatar) {
      return (
        <>
          {thirdAvatar?.profile_img_v2.includes('json') ? (
            <div
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 0 }}
            >
              {noneAvatar}
            </div>
          ) : (
            <Avatars
              colors={colors}
              mode='camera'
              name={thirdAvatar?.dev_name}
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 0 }}
              imgSrc={thirdAvatar?.profile_img_v2 || undefined}
            />
          )}
          {secondsAvatar?.profile_img_v2.includes('json') ? (
            <div
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 12 }}
            >
              {noneAvatar}
            </div>
          ) : (
            <Avatars
              colors={colors}
              mode='camera'
              name={secondsAvatar?.dev_name}
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 12 }}
              imgSrc={secondsAvatar?.profile_img_v2 || undefined}
            />
          )}
          {firstAvatar?.profile_img_v2.includes('json') ? (
            <div
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 24 }}
            >
              {noneAvatar}
            </div>
          ) : (
            <Avatars
              colors={colors}
              mode='camera'
              name={firstAvatar?.dev_name}
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 24 }}
              imgSrc={firstAvatar?.profile_img_v2 || undefined}
            />
          )}
        </>
      );
    } else if (firstAvatar && secondsAvatar) {
      return (
        <>
          {secondsAvatar?.profile_img_v2.includes('json') ? (
            <div
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 0 }}
            >
              {noneAvatar}
            </div>
          ) : (
            <Avatars
              colors={colors}
              mode='camera'
              name={secondsAvatar?.dev_name}
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 0 }}
              imgSrc={secondsAvatar?.profile_img_v2 || undefined}
            />
          )}
          {firstAvatar?.profile_img_v2.includes('json') ? (
            <div
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 12 }}
            >
              {noneAvatar}
            </div>
          ) : (
            <Avatars
              colors={colors}
              mode='camera'
              name={firstAvatar?.dev_name}
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 12 }}
              imgSrc={firstAvatar?.profile_img_v2 || undefined}
            />
          )}
        </>
      );
    } else {
      return (
        <>
          {firstAvatar?.profile_img_v2.includes('json') ? (
            <div
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 0 }}
            >
              {noneAvatar}
            </div>
          ) : (
            <Avatars
              colors={colors}
              mode='camera'
              name={firstAvatar?.dev_name}
              className={classes.avatarMulti2}
              style={{ bottom: 6, left: 0 }}
              imgSrc={firstAvatar?.profile_img_v2 || undefined}
            />
          )}
        </>
      );
    }
  }, [classes.avatarMulti2, colors, group.GroupID, groupCams]);

  const cameraCountText = useMemo(() => {
    // mantis - 10483, 할당된 카메라 없는 경우 0 Cameras로 출력 (Leehj)
    if (!group.DashCamUser) return `0 ${t('Cameras')}`;

    const cams = _.filter(
      groupCams,
      (d) => d.groupManagementID === group.GroupID
    );

    if (cams.length === 1) {
      return `${cams.length} ${t('Camera')}`;
    }
    return `${cams.length} ${t('Cameras')}`;
  }, [group.DashCamUser, group.GroupID, groupCams, t]);

  return (
    <div
      className={clsx(classes.closeHeader, {
        [classes.openHeader]: open,
      })}
    >
      <div
        style={
          color === 'dark'
            ? {
                backgroundColor: SemanticDarkColors.primary['1'],
                borderRadius: open ? '16px 16px 0 0' : 16,
                borderTop: open ? `1px solid ${colors.primary['6']}` : 'none',
              }
            : {}
        }
      >
        <div className={classes.status}>
          <div className={classes.noti}>
            <Typography
              category='Default'
              variant='BodyBold'
              className={classes.groupTitle}
              htmlColor={colors.primary['2']}
            >
              {group.GroupName}
            </Typography>

            {open ? (
              <KeyboardArrowUpIcon
                onClick={() => {
                  setOpen(false);
                  onClose();
                  // setCameraCardExtraHeight([]);

                  // handleOpenGroupPanel?.({ psn: group.GroupID, height: 0 });

                  // if (mobile) {
                  //   setTimeout(() => {
                  //     handleMobileMoveTop(cardRef);
                  //   }, 200);
                  // } else {
                  //   setTimeout(() => {
                  //     handleMoveTop(cardRef.current);
                  //   }, 200);
                  // }

                  // setTimeout(() => {
                  //   setShowFixedHeader(false);
                  //   setIsAbsoluteHeader(false);
                  // }, 300);
                }}
                className={classes.arrow}
              />
            ) : (
              <KeyboardArrowDownIcon
                onClick={() => {
                  setOpen(true);
                  onOpen();

                  // handleOpenGroupPanel?.({
                  //   psn: group.GroupID,
                  //   height: Math.min(
                  //     maxHeight - 110 - 16,
                  //     filteredCams.length * 230 +
                  //       cameraCardExtraHeight.reduce(
                  //         (acc, cur) => acc + cur.height,
                  //         0
                  //       )
                  //   ),
                  // });
                  // if (mobile) {
                  //   setTimeout(() => {
                  //     handleMobileMoveTop(cardRef);
                  //   }, 200);
                  // } else {
                  //   setTimeout(() => {
                  //     handleMoveTop(cardRef.current);
                  //   }, 200);
                  // }
                }}
                className={classes.arrow}
              />
            )}
          </div>
        </div>
        <div className={classes.profile}>
          <div className={classes.avatar}>
            <div
              style={{
                position: 'relative',
                width: avartarDivWidth,
                height: 34,
              }}
            >
              {avatarMarkup}
            </div>

            <div className={classes.cameraCountDiv}>
              <Typography
                category='Default'
                variant='Caption'
                htmlColor={colors.primary['1']}
                style={{
                  wordBreak: 'break-all',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {cameraCountText}
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
